import {
  EvaluableType,
  SampleError,
} from "@/services/backend/sample-error/sample-error";
import {
  ListRequest,
  useListSampleErrorsQuery,
} from "@/services/backend/sample-error/service";
import { Sample, SampleSet } from "@/services/backend/sample-set/sample-set";
import {
  ListRequest as ListSampleSetsRequest,
  UpdateSampleRequest,
  UpdateSamplesRequest,
  useSampleSetListQuery,
  useUpdateSamplesMutation,
} from "@/services/backend/sample-set/service";
import { GrowthMedium, SampleType } from "@/services/backend/types";
import { growthMedia, sampleTypes } from "@/shared/components/enum-labels";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";
import { ArrowRight, CheckCircle2, X } from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { SampleSetNumber } from "../sample-set/sample-set-number";
import { DeletableBadge } from "./deleteable-error-badge";
import EvaluableBadge from "./evaluable-badge";

interface SampleViewModel {
  sampleSetId: string;
  sample: Sample;
  sampleType: SampleType;
}

interface ErrorAssignmentDialogProps {
  disabled?: boolean;
  ownerId: string;
  ownerType: string;
}

function getSampleTypeBadge(
  sets: SampleSet[] | null | undefined,
  sampleSetId: string,
): ReactElement {
  const foundSet = (sets ?? []).find((set) => set.id === sampleSetId);
  return (
    <Badge variant="outline" className="text-xs">
      {foundSet ? sampleTypes[foundSet.sampleType] : "Kein Typ vorhanden"}
    </Badge>
  );
}

export function ErrorAssignmentDialog({
  ownerId,
  ownerType,
  disabled,
}: ErrorAssignmentDialogProps) {
  const [updateSample, { error }] = useUpdateSamplesMutation();
  const [selectedError, setSelectedError] = useState<string>("");
  const [availableItems, setAvailableItems] = useState<SampleViewModel[]>([]);
  const [assignedItems, setAssignedItems] = useState<SampleViewModel[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [growthMedium, setGrowthMedium] = useState<string>("all");
  const [sampleType, setSampleType] = useState<string>("all");
  // Tracks in-progress local assignments across selectedError changes.
  // Key: sample.id — Value: desired SampleError[] for that sample.
  const [pendingErrors, setPendingErrors] = useState<
    Map<string, SampleError[]>
  >(new Map());
  const [request, setRequest] = useState<ListRequest>({});
  const [sampleSetRequest, setSampleSetRequest] =
    useState<ListSampleSetsRequest>({
      sampleType: {
        active: sampleType !== "all",
        values: sampleType !== "all" ? [sampleType as SampleType] : [],
      },
      ownerId: { active: true, values: [ownerId] },
      ownerType: { active: true, values: [ownerType] },
      limit: 0,
      offset: 0,
    });

  const { data: sampleErrors } = useListSampleErrorsQuery(request);
  const { data: sets } = useSampleSetListQuery(sampleSetRequest);

  const getError = (id?: string) =>
    sampleErrors?.items.find((se) => se.id === (id ?? selectedError));

  useEffect(() => {
    if (!sampleErrors) return;

    const sampleError = sampleErrors.items.find(
      (se) => se.id === selectedError,
    );

    const all: SampleViewModel[] = (sets?.items ?? []).flatMap((set) =>
      (set.samples ?? []).map((s) => ({
        sampleSetId: set.id,
        // Apply pending overrides; fall back to server data.
        sample: {
          ...s,
          sampleErrors: pendingErrors.has(s.id)
            ? pendingErrors.get(s.id)!
            : s.sampleErrors,
        },
        sampleType: set.sampleType,
      })),
    );

    const filtered = all.filter((vm) =>
      growthMedium !== "all" ? vm.sample.growthMedium === growthMedium : true,
    );

    const available = filtered.filter(
      (svm) =>
        !svm.sample.sampleErrors.some(
          (err) => err.originalId === sampleError?.originalId,
        ),
    );

    const assigned = filtered.filter(
      (svm) => svm.sample.sampleErrors.length > 0,
    );

    setAvailableItems(available);
    setAssignedItems(assigned);

    // keep selection only for items that got actually assigned elsewhere,
    // not ones merely hidden by the current display filters
    setSelectedTargets((prev) =>
      prev.filter((id) => available.some((vm) => vm.sample.id === id)),
    );
  }, [
    sets,
    growthMedium,
    sampleType,
    sampleErrors,
    selectedError,
    pendingErrors,
  ]);

  useEffect(() => {
    if (sampleErrors != null && sampleErrors.count > 0) {
      setSelectedError(sampleErrors.items[0].id);
    }
  }, [sampleErrors]);

  // Prune a pending override once the refetched server data already reflects it.
  // Clearing pendingErrors eagerly on save would bounce items between panels while
  // the refetch is in flight; dropping entries only when the server matches keeps
  // the merged view stable.
  useEffect(() => {
    if (!sets?.items) return;
    setPendingErrors((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map(prev);
      let changed = false;
      sets.items.forEach((set) =>
        (set.samples ?? []).forEach((s) => {
          if (!next.has(s.id)) return;
          const desiredIds = new Set(next.get(s.id)!.map((e) => e.originalId));
          const serverIds = new Set(
            (s.sampleErrors ?? []).map((e) => e.originalId),
          );
          const matches =
            desiredIds.size === serverIds.size &&
            [...desiredIds].every((id) => serverIds.has(id));
          if (matches) {
            next.delete(s.id);
            changed = true;
          }
        }),
      );
      return changed ? next : prev;
    });
  }, [sets]);

  useEffect(() => {
    setRequest((r) => ({
      ...r,
      growthMedium: {
        active: growthMedium !== "all",
        values: growthMedium !== "all" ? [growthMedium as GrowthMedium] : [],
      },
      sampleType: {
        active: sampleType !== "all",
        values: sampleType !== "all" ? [sampleType as SampleType] : [],
      },
    }));

    setSampleSetRequest((r) => ({
      ...r,
      sampleType: {
        active: sampleType !== "all",
        values: sampleType !== "all" ? [sampleType as SampleType] : [],
      },
    }));
  }, [growthMedium, sampleType]);

  const handleAssignment = () => {
    const err = getError();
    if (!err || selectedTargets.length === 0) return;

    const picked = new Set(selectedTargets);

    setPendingErrors((prev) => {
      const next = new Map(prev);
      availableItems
        .filter((vm) => picked.has(vm.sample.id))
        .forEach((vm) => {
          const alreadyHas = vm.sample.sampleErrors.some(
            (e) => e.originalId === err.originalId,
          );
          if (!alreadyHas) {
            next.set(vm.sample.id, [...vm.sample.sampleErrors, err]);
          }
        });
      return next;
    });

    setSelectedTargets([]);
  };

  const handleUnassign = (sampleId: string, errorOriginalId?: string) => {
    const originalId = errorOriginalId ?? getError()?.originalId;
    if (!originalId) return;

    const vm = assignedItems.find((item) => item.sample.id === sampleId);
    if (!vm) return;

    const nextErrors = vm.sample.sampleErrors.filter(
      (e) => e.originalId !== originalId,
    );

    setPendingErrors((prev) => {
      const next = new Map(prev);
      // Empty array is intentional: explicitly tracks "all errors removed".
      next.set(sampleId, nextErrors);
      return next;
    });
  };

  const onSubmit = () => {
    // Iterate all samples from sets (not just the filtered view) so that
    // samples hidden by growthMedium/sampleType filters are not dropped.
    const samples: UpdateSampleRequest[] = (sets?.items ?? []).flatMap((set) =>
      (set.samples ?? []).map((s) => ({
        sampleSetId: set.id,
        sample: {
          ...s,
          sampleErrors: pendingErrors.has(s.id)
            ? pendingErrors.get(s.id)!
            : s.sampleErrors,
        },
      })),
    );

    const payload: UpdateSamplesRequest = { samples };
    updateSample(payload);
  };

  const allAvailableSelected =
    availableItems.length > 0 &&
    availableItems.every((item) => selectedTargets.includes(item.sample.id));

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <p>Fehler zuweisen</p>
        </Button>
      </DialogTrigger>
      <DialogContent className="flex min-w-full flex-col p-6 md:min-w-full lg:min-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Fehlerzuweisung
          </DialogTitle>
        </DialogHeader>

        <RTKQueryErrorAlert error={error} />

        <div className="flex h-full flex-col space-y-4">
          <div className="flex flex-row space-x-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">Fehler</p>
              <Select
                value={selectedError}
                onValueChange={(value) => {
                  setSelectedError(value);
                }}
              >
                <SelectTrigger className="">
                  <SelectValue placeholder="Select item..." />
                </SelectTrigger>
                <SelectContent>
                  {sampleErrors?.items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.description}</span>
                        <EvaluableBadge evaluableType={item.evaluableType} />
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Nährmedium</p>
              <Select value={growthMedium} onValueChange={setGrowthMedium}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All types..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {Object.values(GrowthMedium).map((value) => (
                    <SelectItem key={value} value={value}>
                      {growthMedia[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Probentyp</p>
              <Select value={sampleType} onValueChange={setSampleType}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {Object.values(SampleType).map((value) => (
                    <SelectItem key={value} value={value}>
                      {sampleTypes[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="invisible text-sm font-medium">Filter</p>
              <Button
                variant="ghost"
                disabled={growthMedium === "all" && sampleType === "all"}
                onClick={() => {
                  setGrowthMedium("all");
                  setSampleType("all");
                }}
              >
                <X className="h-4 w-4" />
                Filter zurücksetzen
              </Button>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            <div className="grid h-full grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Verfügbare Proben</h3>
                  {availableItems.length > 0 && (
                    <button
                      type="button"
                      className="flex cursor-pointer items-center space-x-2"
                      onClick={() => {
                        const ids = availableItems.map(
                          (item) => item.sample.id,
                        );
                        if (allAvailableSelected) {
                          setSelectedTargets((prev) =>
                            prev.filter((id) => !ids.includes(id)),
                          );
                        } else {
                          setSelectedTargets((prev) => [
                            ...new Set([...prev, ...ids]),
                          ]);
                        }
                      }}
                    >
                      <Checkbox checked={allAvailableSelected} />
                      <span className="text-sm">Alle auswählen</span>
                    </button>
                  )}
                </div>
                <div className="flex-1 rounded-lg border">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 p-4">
                      {availableItems.length === 0 ? (
                        <div className="text-muted-foreground py-12 text-center">
                          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 opacity-50" />
                          <p className="font-medium">
                            Keine passenden Proben vorhanden
                          </p>
                          <p className="text-sm">
                            Passen Sie die Filter entsprechend an
                          </p>
                        </div>
                      ) : (
                        availableItems?.map((item) => (
                          <div
                            key={item.sample.id}
                            className={cn(
                              "hover:bg-muted/50 hover:border-border bg-muted/30 flex items-center space-x-3 rounded-md border border-transparent p-3 transition-all duration-200",
                            )}
                          >
                            <Checkbox
                              id={item.sample.id}
                              checked={selectedTargets.includes(item.sample.id)}
                              onCheckedChange={(checked) => {
                                setSelectedTargets((prev) => {
                                  if (checked) {
                                    if (prev.includes(item.sample.id))
                                      return prev;
                                    return [...prev, item.sample.id];
                                  }
                                  return prev.filter(
                                    (id) => id !== item.sample.id,
                                  );
                                });
                              }}
                            />
                            <label
                              htmlFor={item.sample.id}
                              aria-label={growthMedia[item.sample.growthMedium]}
                              className="flex-1 cursor-pointer"
                            >
                              <div
                                className={cn(
                                  "flex items-center justify-between",
                                )}
                              >
                                <p className="flex flex-row space-x-2">
                                  <div>
                                    <SampleSetNumber
                                      sampleSetId={item.sampleSetId}
                                    />
                                    -{growthMedia[item.sample.growthMedium]}
                                  </div>
                                </p>
                                <div className="flex gap-1">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {growthMedia[item.sample.growthMedium]}
                                  </Badge>
                                  {getSampleTypeBadge(
                                    sets?.items ?? [],
                                    item.sampleSetId,
                                  )}
                                </div>
                              </div>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <div className="flex justify-center pt-2">
                  <Button
                    onClick={handleAssignment}
                    disabled={!selectedError || selectedTargets.length === 0}
                    className="h-12 w-full px-8 text-base"
                    size="lg"
                  >
                    ({selectedTargets.length}) Fehler zuweisen
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Aktuelle Zuweisungen
                  </h3>
                  <Badge variant="secondary">
                    {assignedItems.length} total
                  </Badge>
                </div>

                <div className="flex-1 rounded-lg border">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 p-4">
                      {assignedItems.length === 0 ? (
                        <div className="text-muted-foreground py-12 text-center">
                          <ArrowRight className="mx-auto mb-2 h-8 w-8 opacity-50" />
                          <p className="font-medium">
                            Noch keine Zuweisungen vorhanden
                          </p>
                          <p className="text-sm">
                            Proben auswählen und einem Fehler zuweisen
                          </p>
                        </div>
                      ) : (
                        assignedItems.map((assignment) => (
                          <div
                            key={assignment.sample.id}
                            className="bg-muted/30 hover:bg-muted/50 hover:border-border flex items-center justify-between rounded-md border border-transparent p-3 transition-colors"
                          >
                            <div className="flex w-full flex-col items-center gap-3">
                              <span className="w-full">
                                <div className="flex w-full items-center justify-between">
                                  <p className="flex flex-row space-x-2">
                                    <SampleSetNumber
                                      sampleSetId={assignment.sampleSetId}
                                    />
                                    -
                                    {
                                      growthMedia[
                                        assignment.sample.growthMedium
                                      ]
                                    }
                                  </p>
                                  <div className="flex gap-1">
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {
                                        growthMedia[
                                          assignment.sample.growthMedium
                                        ]
                                      }
                                    </Badge>
                                    {getSampleTypeBadge(
                                      sets?.items ?? [],
                                      assignment.sampleSetId,
                                    )}
                                  </div>
                                </div>
                              </span>
                              <span className="w-full items-start justify-center space-x-2">
                                {assignment.sample.sampleErrors.map((err) => (
                                  <DeletableBadge
                                    key={err.originalId}
                                    className={cn("text-white")}
                                    variant={
                                      err.evaluableType ===
                                      EvaluableType.Evaluable
                                        ? "ok"
                                        : "destructive"
                                    }
                                    onDelete={() =>
                                      handleUnassign(
                                        assignment.sample.id,
                                        err.originalId,
                                      )
                                    }
                                  >
                                    <p>{err.description}</p>
                                  </DeletableBadge>
                                ))}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <div className="flex justify-center pt-2">
                  <Button
                    onClick={onSubmit}
                    className="h-12 w-full px-8 text-base"
                    size="lg"
                  >
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
